import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'

import ArrowIcon from '../../components/atoms/Arrow'
import Text from '../../components/atoms/Text'
import Button from '../../components/atoms/GoodFlipButton'

import CaloriesIcon from '../../assets/icons/fire.png'
import GIIcon from '../../assets/icons/GI_Icon.png'
import CancelIcon from '../../assets/icons/cancel_icon.svg'
import PotassiumSummaryCard from '../../components/molecules/Result/PotassiumSummaryCard'
const FoodBreakDown = forwardRef(
  ({ selectedFood, onClickViewMore = () => {} }, ref) => {
    const [isOpen, setIsOpen] = useState(false)
    const divRef = useRef(null)
    useImperativeHandle(ref, () => ({
      show: () => setIsOpen(true),
      hide: () => setIsOpen(false),
    }))

    useEffect(() => {
      if (isOpen) {
        setTimeout(() => {
          divRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
        }, 100)
      }
    }, [isOpen])

    const getTag = (grade) => {
      switch (grade) {
        case 'A':
          return 'Highly Recommended'
        case 'B':
          return 'Good in Moderation'
        case 'C':
          return 'Consume with Care'
        case 'D':
          return 'Avoid if Possible'
      }
    }

    const fetchColorFromGrade = (grade) => {
      switch (grade) {
        case 'A':
          return {
            borderClass: 'border-SUCCESS-600',
            bgClass: 'from-SUCCESS-200',
            gradeText: 'text-brand-secondary',
            gradeBg: 'bg-BRAND-100',
          }
        case 'B':
          return {
            borderClass: 'border-GOLD-600',
            bgClass: 'from-GOLD-200',
            gradeText: 'text-GOLD-900',
            gradeBg: 'bg-background-accent-gold',
          }
        case 'C':
          return {
            borderClass: 'border-WARNING-600',
            bgClass: 'from-WARNING-200',
            gradeText: 'text-WARNING-700',
            gradeBg: 'bg-WARNING-100',
          }
        case 'D':
          return {
            borderClass: 'border-ERROR-600',
            bgClass: 'from-ERROR-300',
            gradeText: 'text-ERROR-900',
            gradeBg: 'bg-ERROR-100',
          }
        default:
          return {
            borderClass: 'border-SUCCESS-600',
            bgClass: 'from-SUCCESS-200',
            gradeText: 'text-brand-secondary',
            gradeBg: 'bg-BRAND-100',
          }
      }
    }
    let gradeClass = fetchColorFromGrade(selectedFood?.grade || 'B')

    const renderNutrientCard = (value, label, score = 0) => {
      if (score === 0) {
        return (
          <div className="border w-[calc(50%-10px)] border-border-secondary px-2 py-4 rounded-xl">
            <div className="flex flex-col justify-between">
              <Text variant="title" size="medium" tag="p">
                {value}
              </Text>
              <Text
                variant="body"
                size="medium"
                tag="div"
                className={classNames(
                  'px-2 mt-3 py-[1px] text-ERROR-600 bg-ERROR-100 rounded-2xl w-fit',
                )}
              >
                {label}
              </Text>
            </div>
          </div>
        )
      } else {
        return (
          <div className="border w-[calc(50%-10px)] border-border-secondary px-2 py-4 rounded-xl">
            <div className="flex flex-col justify-between">
              <Text variant="title" size="medium" tag="p">
                {value}
              </Text>
              <Text
                variant="body"
                size="medium"
                tag="div"
                className={classNames(
                  'px-2 mt-3 py-[1px] bg-SUCCESS-100 text-text-success-primary rounded-2xl w-fit',
                )}
              >
                {label}
              </Text>
            </div>
          </div>
        )
      }
    }
    if (!selectedFood) return <></>
    return (
      <div
        className={classNames(
          `transition-all duration-700 transform  fixed top-0 left-0 right-0 h-screen overflow-y-scroll max-w-lg lg:max-w-6xl mx-auto bg-white`,
          isOpen ? 'translate-y-0 z-[999]' : 'translate-y-[100%] z-[0]',
        )}
        ref={divRef}
      >
        <div className={``}>
          <div className="pt-2 w-full relative">
            <div
              className="absolute z-[10] right-5 p-2 rounded-full shadow-xl w-9 h-9 flex items-center justify-start "
              onClick={() => setIsOpen(false)}
            >
              <img src={CancelIcon} />
            </div>
          </div>
          <div className="p-4 mt-4">
            <Text variant="headline" size="large">
              Food Breakdown
            </Text>
          </div>
          <div
            className={classNames(
              'border-t-2  bg-gradient-to-b  to-white relative',
              gradeClass.bgClass,
              gradeClass.borderClass,
            )}
          >
            <Text
              variant="label"
              size="medium"
              className={classNames(
                ' py-1 px-4 rounded-br-2xl !font-bold',
                gradeClass.gradeText,
                gradeClass.gradeBg,
              )}
            >
              Grade {selectedFood.grade}
            </Text>
            <div className="p-4 pt-6">
              <Text
                variant="title"
                size="large"
                className={'text-text-primary'}
                tag="p"
              >
                {selectedFood.food_details.food_item
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())}
              </Text>
              <Text
                variant="label"
                size="small"
                className={
                  'bg-white text-text-primary mt-2 py-1 px-1.5 rounded-xl !font-bold'
                }
              >
                {getTag(selectedFood.grade)}
              </Text>
              <Text
                tag="p"
                variant="body"
                size="medium"
                className={'text-text-quartenary !text-sm mt-2'}
              >
                {(selectedFood.suggestions || []).join('')}
              </Text>
            </div>
          </div>

          <div className="p-4 mt-8 flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <img src={CaloriesIcon} className="h-9 w-9" />
              <div className="text-text-primary flex gap-2">
                <Text variant="body" size="large">
                  Calories
                </Text>
                <Text variant="title" size="small">
                  {selectedFood?.food_details?.calorie.value} kcal
                </Text>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <img src={GIIcon} className="h-9 w-9" />
              <div className="text-text-primary flex gap-2">
                <Text variant="body" size="large">
                  Glycemic Index
                </Text>
                <Text variant="title" size="small">
                  {
                    selectedFood?.food_details.micronutrients?.glycemic_index
                      .value
                  }
                </Text>
              </div>
            </div>
            {selectedFood?.key_nutrients?.water_content && (
              <div className="flex items-center gap-2">
                <div className="h-9 w-9 flex items-center justify-center text-lg">
                  💧
                </div>
                <div className="text-text-primary flex gap-2">
                  <Text variant="body" size="large">
                    Water Content
                  </Text>
                  <Text variant="title" size="small">
                    {selectedFood.key_nutrients.water_content.value}{' '}
                    {selectedFood.key_nutrients.water_content.unit}
                  </Text>
                </div>
              </div>
            )}

            {selectedFood?.potassium_summary && (
              <PotassiumSummaryCard
                grade={selectedFood.grade}
                title="Potassium Guidance"
                summary={selectedFood.potassium_summary}
                className="mt-4"
              />
            )}

            <div className="p-4 mt-6">
              <div className="flex items-center">
                <Text variant="label" size="small">
                  Macronutrients
                </Text>
                <div className="w-full h-[2px] bg-border-secondary"></div>
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                {/* <div className="border w-[170px] border-border-secondary px-2 py-4 rounded-xl">
                <div className="flex flex-col justify-between">
                  <Text variant="title" size="medium" tag="p">
                    12g
                  </Text>
                  <Text
                    variant="body"
                    size="medium"
                    tag="div"
                    className={
                      'px-2 mt-3 py-[1px] bg-SUCCESS-100 text-text-success-primary rounded-2xl w-fit'
                    }
                  >
                    Protein
                  </Text>
                </div>
              </div> */}
                {Object.keys(selectedFood?.food_details?.macronutrients).map(
                  (key) => {
                    let data = selectedFood?.food_details?.macronutrients[key]
                    return renderNutrientCard(
                      `${data.value}g`,
                      key
                        .toLowerCase()
                        .replace(/\b\w/g, (char) => char.toUpperCase()),
                      data.status,
                    )
                  },
                )}
              </div>
            </div>
            <div className="p-4 mt-6">
              <div className="flex items-center">
                <Text variant="label" size="small">
                  Micronutrients
                </Text>
                <div className="w-full h-[2px] bg-border-secondary"></div>
              </div>
              <div className="mt-4 flex gap-3 flex-wrap">
                {Object.keys(
                  selectedFood?.food_details?.micronutrients?.minerals,
                ).map((key) => {
                  let data =
                    selectedFood?.food_details?.micronutrients?.minerals[key]
                  return renderNutrientCard(
                    `${data.value}g`,
                    key
                      .toLowerCase()
                      .replace(/\b\w/g, (char) => char.toUpperCase()),
                    data.status,
                  )
                })}
              </div>
            </div>
            <div className="mt-6">
              <Button
                title={'View All Food Items'}
                onClick={onClickViewMore}
                className="!w-full !m-0 !mt-0"
                type="goodflip_primary"
                rightIcon={<ArrowIcon direction="right" />}
                leftIcon={null}
              ></Button>
            </div>
          </div>
        </div>
      </div>
    )
  },
)

export default FoodBreakDown
